import { FormInput, FormLabel, FormSelect, FormTextarea } from "@/components";
import { Button, Label, RadioGroup, RadioGroupItem } from "@/components/ui";
import { Bookmark } from "lucide-react";
import { FC } from "react";
import { Controller, FormProvider } from "react-hook-form";
import {
  COMMENT,
  CRM_OPS_STATUS,
  crmOpsStatusOptions,
  REGION_HEAD_ID,
  RELATIONSHIP_MANAGER_ID,
  SCHOOL_EDUCATOR_EMAIL,
  SCHOOL_EDUCATOR_MOBILE,
  SCHOOL_EDUCATOR_NAME
} from "./consts";
import { useAddEditResource } from "./useAddEditResource";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import { LOGIN_ROLES } from "@/lib/utils";

const AddEditResource: FC = () => {
  const {
    form,
    onSubmit,
    isSubmitting,
    regionHeadOptions,
    relationshipManagerOptions,
    isLoadingRegionHeads,
    isLoadingRelationshipManagers,
    selectedRegionHead,
    selectedRelationshipManager,
    isEditMode,
    isLoadingExisting,
  } = useAddEditResource();

  const { handleSubmit, control, formState } = form;
  const { errors } = formState;

  const loggedInUserData = useSelector(
    (state: RootState) => state.currentData.loggedInUserData
  );

  const roles2 = loggedInUserData?.role || [];

  // Helper to check if user has a given role
  const hasRole = (role: string) =>
    Array.isArray(roles2) && roles2.includes(role);

  return (
    <FormProvider {...form}>
      {isLoadingExisting ? (
        <div className="flex justify-center items-center py-8">
          <div className="text-gray-500">Loading existing data...</div>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="grid gap-4">
          {/* Region Head Section */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-[18px] text-primary mb-5">
              CRM Ops Status & Resource Allocation Info
            </h2>
            {(hasRole(LOGIN_ROLES.SUPER_ADMIN) ||
              hasRole(LOGIN_ROLES.CRM_HEAD)) && (
                <Button
                  type="submit"
                  variant="default"
                  disabled={isSubmitting}
                >
                  Save
                </Button>
              )}
          </div>
          <fieldset
            className="flex flex-wrap gap-5"
            disabled={
              !hasRole(LOGIN_ROLES.SALES_OPS) &&
              !hasRole(LOGIN_ROLES.SUPER_ADMIN) &&
              !hasRole(LOGIN_ROLES.FINANCE_TEAM) &&
              !hasRole(LOGIN_ROLES.CRM_HEAD)
            }
          >
            {/* Region Head Section */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormSelect
                  name={REGION_HEAD_ID}
                  control={control}
                  label="Region Head Name"
                  placeholder="Please select"
                  error={errors[REGION_HEAD_ID]?.message as string}
                  options={regionHeadOptions}
                  isRequired
                  isLoading={isLoadingRegionHeads}
                />
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700">
                    Mobile No.
                  </label>
                  <input
                    type="text"
                    placeholder="Autopopulated"
                    value={selectedRegionHead?.mobile_number || ""}
                    disabled={true}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700">
                    Email ID
                  </label>
                  <input
                    type="text"
                    placeholder="Autopopulated"
                    value={selectedRegionHead?.email || ""}
                    disabled={true}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed"
                  />
                </div>
              </div>
            </div>

            {/* Relationship Manager Section */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormSelect
                  name={RELATIONSHIP_MANAGER_ID}
                  control={control}
                  label="Relationship Manager Name"
                  placeholder="Please select"
                  error={errors[RELATIONSHIP_MANAGER_ID]?.message as string}
                  options={relationshipManagerOptions}
                  isRequired
                  isLoading={isLoadingRelationshipManagers}
                />
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700">
                    Mobile No.
                  </label>
                  <input
                    type="text"
                    placeholder="Autopopulated"
                    value={selectedRelationshipManager?.mobile_number || ""}
                    disabled={true}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700">
                    Email ID
                  </label>
                  <input
                    type="text"
                    placeholder="Autopopulated"
                    value={selectedRelationshipManager?.email || ""}
                    disabled={true}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed"
                  />
                </div>
              </div>
            </div>

            {/* School Educator Name Section */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormInput
                  name={SCHOOL_EDUCATOR_NAME}
                  control={form.control}
                  label="School Educator Name"
                  placeholder="Please enter"
                  error={errors[SCHOOL_EDUCATOR_NAME]?.message}
                  isRequired
                />
                <div className="space-y-1">
                  <FormInput
                    name={SCHOOL_EDUCATOR_MOBILE}
                    control={form.control}
                    label="Mobile No."
                    placeholder="Please enter"
                    error={errors[SCHOOL_EDUCATOR_MOBILE]?.message}
                    isRequired
                  />
                </div>
                <div className="space-y-1">
                  <FormInput
                    name={SCHOOL_EDUCATOR_EMAIL}
                    control={form.control}
                    label="Email ID"
                    placeholder="Please enter"
                    error={errors[SCHOOL_EDUCATOR_EMAIL]?.message}
                    isRequired
                  />
                </div>
              </div>


              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
                {/* CRM Ops Status - 4 columns */}
                <div className="md:col-span-4 my-4">
                  <FormLabel className="font-medium">
                    CRM Ops Status <span className="text-red-700">*</span>
                  </FormLabel>

                  <Controller
                    name={CRM_OPS_STATUS}
                    control={control}
                    render={({ field }) => (
                      <RadioGroup
                        className="flex flex-wrap gap-x-6 gap-y-3"
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        {crmOpsStatusOptions.map((option) => {
                          const colorMap = {
                            Green: "text-green-600",
                            Yellow: "text-yellow-500",
                            Red: "text-red-600",
                          };
                          return (
                            <div
                              key={option.value}
                              className="flex items-center gap-2"
                            >
                              <RadioGroupItem
                                value={option.value}
                                id={`crm-status-${option.value}`}
                              />
                              <Label
                                htmlFor={`crm-status-${option.value}`}
                                className={`${colorMap[option.value as keyof typeof colorMap]
                                  } text-base cursor-pointer`}
                              >
                                {option.label}
                              </Label>
                            </div>
                          );
                        })}
                      </RadioGroup>
                    )}
                  />
                </div>

                {/* Comment - 8 columns */}
                <div className="md:col-span-8">
                  <FormTextarea
                    name={COMMENT}
                    control={control}
                    label="Comment"
                    placeholder="Write comment here"
                    error={errors[COMMENT]?.message as string}
                    className="min-h-[100px]"
                  />
                </div>
              </div>
            </div>
          </fieldset>
        </form>
      )}
    </FormProvider>
  );
};

export default AddEditResource;
